import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Receipt, Sparkles, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { getPlanInfo, formatDate } from "@/lib/plan";

const PKG_LABELS = { monthly: "Pro Monthly", annual: "Pro Annual", lifetime: "Pro Lifetime" };

export default function BillingCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const plan = getPlanInfo(user);

  useEffect(() => {
    api.get("/payments/history").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);

  const paid = history.filter((h) => h.payment_status === "paid");

  return (
    <div className="tcc-card p-6" data-testid="billing-card">
      <div className="flex items-center gap-2 mb-5">
        <Crown size={19} className="text-primary" />
        <h2 className="font-heading font-bold text-slate-800">Billing & Plan</h2>
      </div>

      <div className={`rounded-2xl p-5 mb-4 ${plan.isPro ? "bg-gradient-to-br from-primary/12 to-accent/20" : "bg-slate-50"}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-heading font-extrabold text-xl text-slate-800" data-testid="billing-plan-name">
                {plan.isPro ? "Pro" : "Free"}
              </span>
              {plan.isPro && <CheckCircle2 size={18} className="text-primary" />}
            </div>
            <p className="text-sm text-slate-500 mt-1" data-testid="billing-plan-status">
              {plan.isLifetime
                ? "Lifetime access — never expires 🎉"
                : plan.isPro
                ? `Renews / expires ${formatDate(plan.expiresAt)}${plan.daysLeft !== null ? ` · ${plan.daysLeft} day${plan.daysLeft === 1 ? "" : "s"} left` : ""}`
                : plan.expired
                ? `Your Pro plan expired ${formatDate(plan.expiresAt)}`
                : "Manual calendar, basic planning & a few resource links."}
            </p>
          </div>
          {!plan.isLifetime && (
            <Button onClick={() => navigate("/pricing")} data-testid="billing-manage-button" className="rounded-full bg-primary hover:bg-primary/90">
              <Sparkles size={16} className="mr-1.5" /> {plan.isPro ? "Renew / change plan" : "Upgrade to Pro"}
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 mt-6">
        <Receipt size={16} className="text-slate-400" />
        <h3 className="font-heading font-semibold text-slate-700 text-sm">Payment history</h3>
      </div>
      {paid.length === 0 ? (
        <p className="text-sm text-slate-400 py-2">No payments yet.</p>
      ) : (
        <div className="space-y-2">
          {paid.map((h) => (
            <div key={h.session_id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 text-sm" data-testid="billing-history-item">
              <div className="flex items-center gap-2">
                <Clock size={14} className="text-slate-400" />
                <span className="text-slate-700 font-medium">{PKG_LABELS[h.package_id] || h.package_id}</span>
                <span className="text-slate-400">· {formatDate(h.created_at)}</span>
              </div>
              <span className="font-semibold text-slate-800">${Number(h.amount).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
